// frontend/src/pages/NotificationAuditPage.jsx
//
// Centro de Notificaciones (Solo Arsenal).
//
// Responde con evidencia, no con suposiciones: qué se debió mandar, a quién, por
// qué canal, si salió y —cuando no salió— por qué exactamente. Todo viene del
// backend; aquí no se decide nada, solo se muestra lo que quedó registrado.
//
// Tres vistas, en el orden en que se suele preguntar:
//   · Envíos: la lista cronológica, para rastrear un caso concreto.
//   · Qué pasó con cada evento: esperado vs enviado por empresa+usuario+evento.
//   · Quién recibe qué: la configuración vigente, para saber si siquiera debía
//     salir. Sin esta última, "no me llegó" se investiga a ciegas.
//
// El lenguaje de la pantalla es el del usuario ("no salió a propósito"); lo
// técnico —estados internos, JSON del proveedor, contexto de la decisión— vive
// en secciones "Información técnica avanzada", contraídas por defecto.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Autocomplete, Box, Button,
  Chip, CircularProgress, Dialog, DialogContent, DialogTitle, Divider, FormControl,
  Grid, IconButton, InputLabel, MenuItem, Paper, Select, Stack, Tab, Table, TableBody,
  TableCell, TableHead, TablePagination, TableRow, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  FilterAltOff as ClearIcon,
  NotificationsActive as BellIcon,
  OpenInNew as OpenIcon,
  Refresh as RefreshIcon,
  Replay as RetryIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';

import NotificationChannelMatrix from '../components/notifications/NotificationChannelMatrix';
import { api } from '../services/api';

// Color e etiqueta por estado. La clave es que "omitido" y "fallido" se vean
// distintos: uno es una decisión deliberada y el otro un problema a resolver.
const STATUS_META = {
  sent: { label: 'Enviado', color: 'success' },
  delivered: { label: 'Entregado', color: 'success' },
  expected: { label: 'Esperado', color: 'info' },
  queued: { label: 'En cola', color: 'info' },
  failed: { label: 'Fallido', color: 'error' },
  provider_error: { label: 'Error proveedor', color: 'error' },
  rate_limited: { label: 'Límite de envío', color: 'warning' },
  template_error: { label: 'Error plantilla', color: 'error' },
  invalid_recipient: { label: 'Sin contacto válido', color: 'warning' },
  blocked_by_preferences: { label: 'Bloqueado por preferencias', color: 'default' },
  blocked_by_quiet_hours: { label: 'Horario silencioso', color: 'default' },
  muted: { label: 'Silenciado', color: 'default' },
  skipped: { label: 'Omitido', color: 'default' },
  duplicate_prevented: { label: 'Duplicado evitado', color: 'default' },
};

const EVENT_LABELS = {
  daily_exception_report: 'Reporte diario por excepción',
  weekly_report: 'Reporte semanal',
  creation: 'Alarma creada',
  acknowledged: 'Ticket reconocido',
  resolved: 'Ticket resuelto',
};

// Los cuatro grupos son los del backend (`_STATUS_GROUPS`), no una agrupación
// propia: así el número del resumen y la tabla que abre al pulsarlo no pueden
// discrepar. `statuses` es solo para repartir los totales que ya vienen.
const RESULT_GROUPS = [
  {
    value: 'sent', label: 'Sí llegaron', color: 'success',
    help: 'Salieron hacia el proveedor sin error.',
    statuses: ['sent', 'delivered'],
  },
  {
    value: 'skipped', label: 'No salieron a propósito', color: 'default',
    help: 'Decisión deliberada: el canal o el evento estaba apagado, era horario silencioso o ya se había mandado.',
    statuses: ['skipped', 'muted', 'blocked_by_preferences', 'blocked_by_quiet_hours', 'duplicate_prevented'],
  },
  {
    value: 'failed', label: 'Requieren atención', color: 'error',
    help: 'No llegaron por un problema: el proveedor las rechazó o el contacto no es válido. Hay que corregirlo.',
    statuses: ['failed', 'provider_error', 'rate_limited', 'template_error', 'invalid_recipient'],
  },
  {
    value: 'expected_not_sent', label: 'Todavía pendientes', color: 'info',
    help: 'Se esperaban y aún no se han intentado o están en cola.',
    statuses: ['expected', 'queued'],
  },
];

const STATUS_GROUPS = [
  { value: '', label: 'Todos' },
  ...RESULT_GROUPS.map(({ value, label }) => ({ value, label })),
];

const EMPTY_FILTERS = {
  company_id: '', user_id: '', event_type: '', channel: '',
  status: '', status_group: '', provider: '', date_from: '', date_to: '', search: '',
};

// Formato que espera <input type="datetime-local">, en hora local. Con
// `toISOString()` a secas la fecha se corría el huso horario completo.
const inputDate = (date) => (
  new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
);

// La pantalla abre acotada a la última semana. Sin esto, el resumen arrancaba
// dominado por miles de fallos antiguos ya resueltos y lo reciente —que es lo
// accionable— quedaba enterrado. El periodo se muestra siempre y se quita con
// un clic; acotar en silencio sería peor que no acotar.
const DEFAULT_DAYS = 7;
const defaultDateFrom = () => inputDate(new Date(Date.now() - DEFAULT_DAYS * 86400000));

const statusChip = (status, extra = {}) => {
  const meta = STATUS_META[status] || { label: status || '—', color: 'default' };
  return <Chip size="small" label={meta.label} color={meta.color} variant={meta.color === 'default' ? 'outlined' : 'filled'} {...extra} />;
};

const resultGroupOf = (status) => RESULT_GROUPS.find((g) => g.statuses.includes(status));

// 'default' es un color de Chip, no del tema: 'default.main' no resuelve y se
// colaría como CSS inválido.
const groupColorToken = (color) => (color === 'default' ? 'text.primary' : `${color}.main`);

const fmtDate = (value) => (value ? new Date(value).toLocaleString('es-MX') : '—');
const eventLabel = (value) => EVENT_LABELS[value] || value || '—';

// Todo lo que solo sirve para depurar va aquí dentro: nace contraído y no
// compite con la lectura normal de la pantalla.
const AdvancedInfo = ({ title, children, sx }) => (
  <Accordion
    disableGutters elevation={0}
    sx={{ border: 1, borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' }, ...sx }}
  >
    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
      <Typography variant="body2" fontWeight={700} color="text.secondary">{title}</Typography>
    </AccordionSummary>
    <AccordionDetails sx={{ pt: 0 }}>{children}</AccordionDetails>
  </Accordion>
);

const NotificationAuditPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [tab, setTab] = useState(0);
  const [filters, setFilters] = useState({
    ...EMPTY_FILTERS,
    // Permite llegar aquí ya filtrado desde la ficha del usuario.
    user_id: searchParams.get('user_id') || '',
    company_id: searchParams.get('company_id') || '',
    date_from: defaultDateFrom(),
  });
  const [options, setOptions] = useState({
    companies: [], users: [], event_types: [], channels: [], providers: [], statuses: [],
  });
  const [data, setData] = useState({ items: [], total: 0, summary: {} });
  const [matrix, setMatrix] = useState({ rows: [] });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);
  // El texto libre se escribe aquí y solo baja a `filters` tras una pausa: sin
  // esto cada tecla lanzaba un ILIKE sobre 8 columnas de toda la tabla.
  const [searchInput, setSearchInput] = useState(filters.search);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => (prev.search === searchInput ? prev : { ...prev, search: searchInput }));
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Solo los filtros con valor viajan al backend: así los parámetros vacíos no
  // se convierten en un filtro por cadena vacía.
  const activeParams = useMemo(() => {
    const out = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== '' && value != null) out[key] = value;
    });
    return out;
  }, [filters]);

  useEffect(() => {
    api.notificationAudit.filters()
      .then((res) => setOptions(res.data))
      .catch(() => { /* los filtros son ayuda, su fallo no bloquea la tabla */ });
  }, []);

  const loadList = useCallback(() => {
    setLoading(true);
    setError(null);
    api.notificationAudit.list({ ...activeParams, page: page + 1, page_size: pageSize })
      .then((res) => setData(res.data))
      .catch((err) => setError(err?.response?.data?.detail || 'No se pudo cargar la auditoría'))
      .finally(() => setLoading(false));
  }, [activeParams, page, pageSize]);

  const loadMatrix = useCallback(() => {
    setLoading(true);
    api.notificationAudit.matrix(activeParams)
      .then((res) => setMatrix(res.data))
      .catch((err) => setError(err?.response?.data?.detail || 'No se pudo cargar la matriz'))
      .finally(() => setLoading(false));
  }, [activeParams]);

  useEffect(() => {
    // La pestaña de configuración se abastece sola y no usa estos filtros: pedir
    // aquí sus datos solo dispararía consultas que nadie mira.
    if (tab === 0) loadList();
    else if (tab === 1) loadMatrix();
  }, [tab, loadList, loadMatrix]);

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const clearFilters = () => {
    setFilters({ ...EMPTY_FILTERS });
    setSearchInput('');
    setSearchParams({});
    setPage(0);
  };

  const openDetail = (id) => {
    setDetailLoading(true);
    setDetail({ id });
    api.notificationAudit.detail(id)
      .then((res) => setDetail(res.data))
      .catch(() => setDetail({ id, _error: 'No se pudo cargar el detalle' }))
      .finally(() => setDetailLoading(false));
  };

  const doRetry = (id) => {
    setActionMsg(null);
    api.notificationAudit.retry(id)
      .then(() => { setActionMsg({ type: 'success', text: 'Reintento ejecutado' }); loadList(); openDetail(id); })
      .catch((err) => setActionMsg({
        type: 'error',
        text: err?.response?.data?.detail || 'No se pudo reintentar',
      }));
  };

  const copy = (value, what) => {
    navigator.clipboard.writeText(typeof value === 'string' ? value : JSON.stringify(value, null, 2))
      .then(() => setActionMsg({ type: 'success', text: `${what} copiado` }))
      .catch(() => setActionMsg({ type: 'error', text: 'No se pudo copiar' }));
  };

  const exportCsv = async () => {
    const { url, token } = api.notificationAudit.exportUrl(activeParams);
    try {
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error('export');
      const blob = await resp.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'auditoria_notificaciones.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      setActionMsg({ type: 'error', text: 'No se pudo exportar' });
    }
  };

  // El resumen reparte los totales del backend en los cuatro grupos. Lo que no
  // encaje en ninguno se cuenta aparte en vez de desaparecer: un resumen que no
  // suma el total es peor que no tener resumen.
  const resultSummary = useMemo(() => {
    const summary = data.summary || {};
    const clasificados = new Set();
    const grupos = RESULT_GROUPS.map((group) => {
      const total = group.statuses.reduce((acc, status) => {
        clasificados.add(status);
        return acc + (summary[status] || 0);
      }, 0);
      return { ...group, total };
    });
    const sinClasificar = Object.entries(summary)
      .filter(([status]) => !clasificados.has(status))
      .reduce((acc, [, count]) => acc + count, 0);
    return { grupos, sinClasificar };
  }, [data.summary]);

  // Canales presentes en la matriz: se derivan de los datos para no inventar
  // columnas de canales que esta instalación no usa.
  const matrixChannels = useMemo(() => {
    const set = new Set();
    (matrix.rows || []).forEach((row) => Object.keys(row.channels || {}).forEach((c) => set.add(c)));
    return Array.from(set).sort();
  }, [matrix.rows]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" sx={{ mb: 2, gap: 1 }}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <BellIcon color="primary" />
            <Typography variant="h5" fontWeight={800}>Centro de Notificaciones</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Qué se debió enviar, qué se envió y por qué no se envió el resto.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {tab !== 2 && <Button size="small" startIcon={<ClearIcon />} onClick={clearFilters}>Limpiar</Button>}
          {tab !== 2 && (
            <Button size="small" startIcon={<RefreshIcon />} onClick={() => (tab === 0 ? loadList() : loadMatrix())}>
              Actualizar
            </Button>
          )}
          {tab === 0 && (
            <Button size="small" variant="contained" startIcon={<DownloadIcon />} onClick={exportCsv}>
              CSV
            </Button>
          )}
        </Stack>
      </Stack>

      {actionMsg && (
        <Alert severity={actionMsg.type} sx={{ mb: 2 }} onClose={() => setActionMsg(null)}>
          {actionMsg.text}
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <AdvancedInfo title="¿Cómo leer esta pantalla?" sx={{ mb: 2 }}>
        <Stack spacing={1.5}>
          <Typography variant="body2">
            Cada fila es <b>un intento por canal</b>: una misma alarma genera una fila para
            el correo, otra para WhatsApp y otra para el portal. Que no exista fila significa
            que nunca se esperó ese envío; que exista y no diga «Sí llegó», que hay un motivo
            registrado.
          </Typography>
          <Box>
            <Typography variant="body2" fontWeight={700} gutterBottom>Las tres pestañas</Typography>
            <Typography variant="body2" component="div">
              <b>Envíos</b>: el detalle cronológico, para seguir un caso concreto.<br />
              <b>Qué pasó con cada evento</b>: por empresa, usuario y evento, el resultado de
              cada canal. Sirve para ver de un vistazo «a esta empresa le llegó correo pero no
              WhatsApp».<br />
              <b>Quién recibe qué</b>: la configuración vigente. Es la pregunta previa: si el
              canal está apagado, no hay envío que buscar.
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" fontWeight={700} gutterBottom>
              Por qué algo no sale (tres interruptores distintos)
            </Typography>
            <Typography variant="body2" component="div">
              1. El <b>canal</b> apagado para esa persona en esa empresa.<br />
              2. El <b>evento</b> con ese canal apagado (puede recibir alarmas por WhatsApp y
              el reporte solo por correo).<br />
              3. El <b>contacto</b> que falta o no es válido: el canal se ve encendido y aun
              así no llega nunca.<br />
              Las preferencias son <b>por usuario y por empresa</b>: la misma persona puede
              tener configuraciones distintas en cada una.
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" fontWeight={700} gutterBottom>Estados internos</Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
              {Object.keys(STATUS_META).map((status) => (
                <Chip key={status} size="small" variant="outlined"
                  color={STATUS_META[status].color}
                  label={`${STATUS_META[status].label} · ${status}`} />
              ))}
            </Stack>
          </Box>
        </Stack>
      </AdvancedInfo>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile sx={{ mb: 2 }}>
        <Tab label="Envíos" />
        <Tab label="Qué pasó con cada evento" />
        <Tab label="Quién recibe qué" />
      </Tabs>

      {tab !== 2 && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <Typography variant="body2" color="text.secondary">Periodo:</Typography>
          {filters.date_from ? (
            <Chip size="small" color="primary" variant="outlined"
              label={`Desde ${new Date(filters.date_from).toLocaleDateString('es-MX')}`}
              onDelete={() => setFilter('date_from', '')} />
          ) : (
            <Chip size="small" variant="outlined" label="Todo el histórico"
              onClick={() => setFilter('date_from', defaultDateFrom())} />
          )}
          {filters.date_to && (
            <Chip size="small" variant="outlined"
              label={`Hasta ${new Date(filters.date_to).toLocaleDateString('es-MX')}`}
              onDelete={() => setFilter('date_to', '')} />
          )}
        </Stack>
      )}

      {tab === 0 && (
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {resultSummary.grupos.map((group) => {
            const activo = filters.status_group === group.value;
            return (
              <Grid item xs={6} md={3} key={group.value}>
                <Tooltip title={group.help}>
                  <Paper
                    variant="outlined"
                    onClick={() => setFilter('status_group', activo ? '' : group.value)}
                    sx={{
                      p: 1.5, cursor: 'pointer', height: '100%',
                      borderColor: activo ? groupColorToken(group.color) : 'divider',
                      borderWidth: activo ? 2 : 1,
                    }}
                  >
                    <Typography variant="h5" fontWeight={800} color={groupColorToken(group.color)}>
                      {group.total}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">{group.label}</Typography>
                  </Paper>
                </Tooltip>
              </Grid>
            );
          })}
          {resultSummary.sinClasificar > 0 && (
            <Grid item xs={12}>
              <Alert severity="warning">
                {resultSummary.sinClasificar} registros con un estado que esta pantalla todavía
                no clasifica. Aparecen en la tabla, pero no en el resumen de arriba.
              </Alert>
            </Grid>
          )}
        </Grid>
      )}

      {tab !== 2 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          {/* Lo que se usa siempre queda a la vista; el resto vive plegado para
              que la pantalla no abra con diez campos vacíos. */}
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                size="small" options={options.companies || []}
                getOptionLabel={(o) => o.name || ''}
                value={(options.companies || []).find((c) => c.id === filters.company_id) || null}
                onChange={(_, v) => setFilter('company_id', v?.id || '')}
                renderInput={(params) => <TextField {...params} label="Empresa" />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {/* El correo va en la etiqueta porque hay cuentas distintas con el
                  mismo nombre; sin él, la lista parecía repetir al mismo usuario. */}
              <Autocomplete
                size="small" options={options.users || []}
                getOptionLabel={(o) => (o.email ? `${o.name} · ${o.email}` : o.name || '')}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={(options.users || []).find((u) => u.id === filters.user_id) || null}
                onChange={(_, v) => setFilter('user_id', v?.id || '')}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" noWrap>{option.name}</Typography>
                      {option.email && (
                        <Typography variant="caption" color="text.secondary" noWrap component="div">
                          {option.email}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
                renderInput={(params) => <TextField {...params} label="Usuario" />}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Resultado</InputLabel>
                <Select label="Resultado" value={filters.status_group} onChange={(e) => setFilter('status_group', e.target.value)}>
                  {STATUS_GROUPS.map((g) => <MenuItem key={g.value} value={g.value}>{g.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField size="small" fullWidth label="Buscar (destinatario, error, asunto…)"
                value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
            </Grid>
          </Grid>

          <AdvancedInfo title="Filtros avanzados" sx={{ mt: 1.5 }}>
            <Grid container spacing={1.5}>
              <Grid item xs={6} sm={4} md={2}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Evento</InputLabel>
                  <Select label="Evento" value={filters.event_type} onChange={(e) => setFilter('event_type', e.target.value)}>
                    <MenuItem value="">Todos</MenuItem>
                    {(options.event_types || []).map((v) => (
                      <MenuItem key={v} value={v}>{eventLabel(v)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Canal</InputLabel>
                  <Select label="Canal" value={filters.channel} onChange={(e) => setFilter('channel', e.target.value)}>
                    <MenuItem value="">Todos</MenuItem>
                    {(options.channels || []).map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Estado exacto</InputLabel>
                  <Select label="Estado exacto" value={filters.status} onChange={(e) => setFilter('status', e.target.value)}>
                    <MenuItem value="">Todos</MenuItem>
                    {(options.statuses || []).map((v) => (
                      <MenuItem key={v} value={v}>{STATUS_META[v]?.label || v}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Proveedor</InputLabel>
                  <Select label="Proveedor" value={filters.provider} onChange={(e) => setFilter('provider', e.target.value)}>
                    <MenuItem value="">Todos</MenuItem>
                    {(options.providers || []).map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <TextField size="small" fullWidth type="datetime-local" label="Desde" InputLabelProps={{ shrink: true }}
                  value={filters.date_from} onChange={(e) => setFilter('date_from', e.target.value)} />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <TextField size="small" fullWidth type="datetime-local" label="Hasta" InputLabelProps={{ shrink: true }}
                  value={filters.date_to} onChange={(e) => setFilter('date_to', e.target.value)} />
              </Grid>
            </Grid>
          </AdvancedInfo>
        </Paper>
      )}


      {loading && tab !== 2 && <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>}

      {!loading && tab === 0 && (
        <Paper variant="outlined">
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Fecha', 'Empresa', 'Usuario', 'Evento', 'Canal', 'Destinatario',
                    'Estado', 'Razón', 'Proveedor', 'Intentos', ''].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.items.length === 0 && (
                  <TableRow><TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">Sin registros para estos filtros</Typography>
                  </TableCell></TableRow>
                )}
                {data.items.map((row) => (
                  <TableRow key={row.id} hover sx={{ cursor: 'pointer' }} onClick={() => openDetail(row.id)}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(row.created_at)}</TableCell>
                    <TableCell>{row.company_name}</TableCell>
                    <TableCell>{row.user_name}</TableCell>
                    <TableCell>{eventLabel(row.event_type)}</TableCell>
                    <TableCell>{row.channel}</TableCell>
                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {row.recipient || '—'}
                    </TableCell>
                    <TableCell>{statusChip(row.status)}</TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>
                      <Tooltip title={row.status_reason || ''}>
                        <Typography variant="caption" sx={{
                          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                        }}>
                          {row.status_reason || '—'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{row.provider || '—'}</TableCell>
                    <TableCell align="center">{row.retry_count}</TableCell>
                    <TableCell align="right">
                      {row.is_retryable && (
                        <Tooltip title="Reintentar">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); doRetry(row.id); }}>
                            <RetryIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          <TablePagination
            component="div" count={data.total} page={page} rowsPerPage={pageSize}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[25, 50, 100, 200]}
            labelRowsPerPage="Filas:"
          />
        </Paper>
      )}

      {!loading && tab === 1 && (
        <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Empresa', 'Usuario', 'Evento'].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                ))}
                {matrixChannels.map((c) => (
                  <TableCell key={c} align="center" sx={{ fontWeight: 700 }}>{c}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {(matrix.rows || []).length === 0 && (
                <TableRow><TableCell colSpan={3 + matrixChannels.length} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Sin registros en el periodo</Typography>
                </TableCell></TableRow>
              )}
              {(matrix.rows || []).map((row) => (
                <TableRow key={`${row.company_id}-${row.user_id}-${row.event_type}`} hover>
                  <TableCell>{row.company_name}</TableCell>
                  <TableCell>{row.user_name}</TableCell>
                  <TableCell>{eventLabel(row.event_type)}</TableCell>
                  {matrixChannels.map((channel) => {
                    const cell = row.channels?.[channel];
                    if (!cell) return <TableCell key={channel} align="center">—</TableCell>;
                    return (
                      <TableCell key={channel} align="center">
                        <Tooltip title={`${cell.reason || ''} · ${fmtDate(cell.at)}`}>
                          {statusChip(cell.status, {
                            onClick: () => openDetail(cell.delivery_id),
                            sx: { cursor: 'pointer' },
                          })}
                        </Tooltip>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {tab === 2 && (
        <Stack spacing={1.5}>
          <Alert severity="info">
            Configuración vigente, no histórico: esto es lo que <b>debería</b> recibir cada
            persona. Si una alarma no llegó y aquí el canal aparece apagado, no hubo envío que
            perder — hay que encenderlo en las preferencias de ese usuario en esa empresa.
          </Alert>
          <NotificationChannelMatrix companyId="all" />
        </Stack>
      )}

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Detalle del envío
          <IconButton onClick={() => setDetail(null)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading && <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress /></Box>}
          {!detailLoading && detail?._error && <Alert severity="error">{detail._error}</Alert>}
          {!detailLoading && detail && !detail._error && detail.status && (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ gap: 1 }}>
                {statusChip(detail.status)}
                <Chip size="small" variant="outlined" label={detail.channel} />
                <Chip size="small" variant="outlined" label={eventLabel(detail.event_type)} />
                {detail.provider && <Chip size="small" variant="outlined" label={detail.provider} />}
              </Stack>

              {/* El titular en el idioma de quien pregunta; el estado interno
                  queda abajo, en la sección técnica. */}
              <Alert severity={resultGroupOf(detail.status)?.color === 'default' ? 'info' : (resultGroupOf(detail.status)?.color || 'info')}>
                <Typography variant="body2" fontWeight={700}>
                  {resultGroupOf(detail.status)?.label || 'Sin clasificar'}
                </Typography>
                {detail.status_reason || 'Sin razón registrada'}
              </Alert>

              <Grid container spacing={1.5}>
                {[
                  ['Empresa', detail.company_name],
                  ['Usuario', detail.user_name],
                  ['Correo del usuario', detail.user_email],
                  ['Destinatario usado', detail.recipient],
                  ['Asunto', detail.subject],
                  ['Intentos', String(detail.retry_count ?? 0)],
                ].map(([label, value]) => (
                  <Grid item xs={12} sm={6} key={label}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>{label}</Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{value || '—'}</Typography>
                  </Grid>
                ))}
              </Grid>

              {(detail.timeline || []).length > 0 && (
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Línea de tiempo</Typography>
                  <Stack spacing={0.5}>
                    {detail.timeline.map((step) => (
                      <Stack key={step.label} direction="row" spacing={1}>
                        <Typography variant="caption" sx={{ minWidth: 140, fontWeight: 700 }}>{step.label}</Typography>
                        <Typography variant="caption" color="text.secondary">{fmtDate(step.at)}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              )}

              {detail.error_message && (
                <Alert severity="error" action={
                  <IconButton size="small" onClick={() => copy(detail.error_message, 'Error')}>
                    <CopyIcon fontSize="small" />
                  </IconButton>
                }>
                  {detail.error_message}
                </Alert>
              )}

              {/* Lo de abajo solo hace falta para depurar: identificadores del
                  proveedor, la preferencia con la que se decidió y la respuesta
                  cruda. Nace contraído para no tapar el motivo, que es lo que
                  casi siempre se viene a buscar. */}
              <AdvancedInfo title="Información técnica avanzada">
                <Stack spacing={2}>
                  <Grid container spacing={1.5}>
                    {[
                      ['Estado interno', detail.status],
                      ['Plantilla', detail.template_name],
                      ['ID del proveedor', detail.provider_message_id],
                      ['Código de error', detail.error_code],
                      ['Próximo reintento', fmtDate(detail.next_retry_at)],
                      ['Referencia', detail.reference_id],
                    ].map(([label, value]) => (
                      <Grid item xs={12} sm={6} key={label}>
                        <Typography variant="caption" color="text.secondary" fontWeight={700}>{label}</Typography>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{value || '—'}</Typography>
                      </Grid>
                    ))}
                  </Grid>

                  {detail.decision_context && (
                    <Box>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="subtitle2" fontWeight={700}>Preferencia evaluada</Typography>
                        <IconButton size="small" onClick={() => copy(detail.decision_context, 'Contexto')}>
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                      <Paper variant="outlined" sx={{ p: 1, bgcolor: 'action.hover', maxHeight: 220, overflow: 'auto' }}>
                        <pre style={{ margin: 0, fontSize: 11, whiteSpace: 'pre-wrap' }}>
                          {JSON.stringify(detail.decision_context, null, 2)}
                        </pre>
                      </Paper>
                    </Box>
                  )}

                  {detail.provider_response && (
                    <Box>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="subtitle2" fontWeight={700}>Respuesta del proveedor</Typography>
                        <IconButton size="small" onClick={() => copy(detail.provider_response, 'Respuesta')}>
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                      <Paper variant="outlined" sx={{ p: 1, bgcolor: 'action.hover', maxHeight: 220, overflow: 'auto' }}>
                        <pre style={{ margin: 0, fontSize: 11, whiteSpace: 'pre-wrap' }}>
                          {JSON.stringify(detail.provider_response, null, 2)}
                        </pre>
                      </Paper>
                    </Box>
                  )}
                </Stack>
              </AdvancedInfo>

              <Divider />
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                {detail.retry_check?.ok ? (
                  <Button size="small" variant="contained" startIcon={<RetryIcon />} onClick={() => doRetry(detail.id)}>
                    Reintentar
                  </Button>
                ) : (
                  <Tooltip title={detail.retry_check?.reason || ''}>
                    <span><Button size="small" disabled startIcon={<RetryIcon />}>Reintentar</Button></span>
                  </Tooltip>
                )}
                {detail.user_id && (
                  <Button size="small" startIcon={<OpenIcon />} onClick={() => navigate(`/users?user=${detail.user_id}`)}>
                    Abrir usuario
                  </Button>
                )}
                {detail.ticket_id && (
                  <Button size="small" startIcon={<OpenIcon />} onClick={() => navigate(`/alarm-tickets/${detail.ticket_id}`)}>
                    Abrir ticket
                  </Button>
                )}
                <Button size="small" onClick={() => api.notificationAudit.review(detail.id, !detail.reviewed_at)
                  .then(() => { setActionMsg({ type: 'success', text: 'Marcado' }); openDetail(detail.id); })}>
                  {detail.reviewed_at ? 'Quitar revisado' : 'Marcar revisado'}
                </Button>
              </Stack>
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default NotificationAuditPage;
